// A system the player neither owns nor shares an alliance with (spec §3.4): what is public, the
// newest intel report, and what stays hidden.
import type { IntelSystem, PublicSystem } from '@stars-and-ships/api-types';
import { CircleHelp } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  FACILITY_LABELS,
  FACILITY_ORDER,
  formatDuration,
  formatShips,
  LATER_FACILITIES,
  RESOURCE_KEYS,
  SYSTEM_TYPE_LABELS,
} from '../../labels';
import { IconButton, Panel, ResourceChip, Sheet, Stat } from '../ui';
import { PlanetsLine } from './shared';

export function ForeignSystemInfo({
  system,
  owner,
  name,
  now,
}: {
  system: PublicSystem | IntelSystem;
  owner: ReactNode;
  name(id: string): string;
  now: number;
}) {
  const [explain, setExplain] = useState(false);
  const intel = 'intel' in system ? system.intel : undefined;
  return (
    <div data-foreign-info>
      <Panel>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Type" value={SYSTEM_TYPE_LABELS[system.systemType]} />
          <Stat label="Owner" value={owner} />
          <Stat
            label="Last intel"
            value={
              intel
                ? `${formatDuration(Math.max(0, now - Date.parse(intel.takenAt)))} ago · ${intel.scoutedBy.displayName}`
                : 'No intel yet'
            }
          />
          <Stat label="Warp gate links" value={system.warpConnections.length} />
        </div>
        <div className="mt-3">
          <PlanetsLine system={system} />
        </div>
        {system.warpConnections.length > 0 ? (
          <p className="mt-1 text-sm text-muted">
            Links to{' '}
            {system.warpConnections.map((w, i) => (
              <span key={w.systemId}>
                {i > 0 ? ', ' : ''}
                <span className="text-text">{name(w.systemId)}</span>
              </span>
            ))}
          </p>
        ) : null}
        <div className="mt-2 flex items-center gap-1 text-xs text-faint">
          <span>
            {intel
              ? 'Facilities, fleets and stock below are from that report, not live.'
              : 'Facilities, fleets and stockpiles are hidden until you scout.'}
          </span>
          <IconButton label="What is hidden?" className="-my-3" onClick={() => setExplain(true)}>
            <CircleHelp className="size-4" aria-hidden />
          </IconButton>
        </div>
        <Sheet open={explain} title="What is hidden" onClose={() => setExplain(false)}>
          <div className="space-y-2 text-sm text-muted">
            <p>
              Only the owner and their alliance see a system live. Everyone else sees its type,
              owner, planets and warp gate links.
            </p>
            <p>
              A scout carrying more Influence than the system stores brings back a report: its
              stock, the fleets stationed there (pirates included) and its facility levels — as they
              were at that moment. Unowned systems always give way.
            </p>
            <p>Reports from your alliance members are shared with you.</p>
          </div>
        </Sheet>
      </Panel>
    </div>
  );
}

/** `intel` tier (04 §3.3): the newest readable report — a snapshot with its age, not live data. */
export function IntelReport({ system }: { system: IntelSystem }) {
  const { snapshot } = system.intel;
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-intel-report={system.intel.reportId}>
      <Panel title={system.owner ? 'Storage' : 'Stockpile'} className="lg:col-span-2">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {RESOURCE_KEYS.map((key) => (
            <div key={key} className="flex min-w-0">
              <ResourceChip resource={key} amount={snapshot.resources[key]} />
            </div>
          ))}
          <div className="flex min-w-0">
            <ResourceChip resource="influence" amount={snapshot.influence} />
          </div>
        </div>
      </Panel>
      <Panel title="Stationed ships">
        {snapshot.fleets.length === 0 ? (
          <p className="text-sm text-muted">No ships stationed.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {snapshot.fleets.map((f) => (
              <li
                key={`${f.kind}-${f.ownerPlayerId ?? 'none'}-${formatShips(f.ships)}`}
                data-intel-fleet={f.kind}
                className={`hud-cut-sm border p-3 ${
                  f.kind === 'pirate'
                    ? 'border-danger/50 bg-danger/10 text-danger'
                    : 'border-line bg-void/60 text-text'
                }`}
              >
                <span className="block font-display text-xs font-bold uppercase tracking-[0.14em]">
                  {f.kind === 'pirate'
                    ? 'Pirates'
                    : f.ownerPlayerId === system.owner?.playerId
                      ? 'Garrison'
                      : 'Stationed fleet'}
                </span>
                <span className="font-mono text-xs tabular-nums">
                  {formatShips(f.ships)}
                  {Object.keys(f.stealthShips).length > 0
                    ? ` · stealth: ${formatShips(f.stealthShips)}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Facilities">
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {FACILITY_ORDER.filter((f) => !LATER_FACILITIES.has(f)).map((facility) => (
            <li key={facility} className="flex justify-between gap-2">
              <span className="truncate text-muted">{FACILITY_LABELS[facility]}</span>
              <span className="font-mono text-text">Lv {snapshot.facilities[facility] ?? 0}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
